import { Overlay, OverlayModule, ScrollStrategy } from '@angular/cdk/overlay';
import { CommonModule } from '@angular/common';
import {
    AfterViewInit,
    Component,
    computed,
    effect,
    ElementRef,
    inject,
    input,
    model,
    output,
    signal,
    ViewChild,
    ChangeDetectionStrategy
} from '@angular/core';
import { SearchInputComponent } from 'ngx-lib';
import { LoadingSpinnerComponent } from '../loading-spinner/loading-spinner.component';

export interface IGenericTypeAheadDropdownConfig<T> {
    labelKey: keyof T;
    identityKey: keyof T;
    secondaryLabel?: string;
}

@Component({
    selector: 'app-generic-multiple-selection',
    imports: [CommonModule, OverlayModule, SearchInputComponent, LoadingSpinnerComponent],
    templateUrl: './generic-multiple-selection.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './generic-multiple-selection.component.scss'
})
export class GenericMultipleSelectionComponent<T extends Record<string, any>> implements AfterViewInit {
    @ViewChild('searchInput')
    searchInput?: SearchInputComponent;
    @ViewChild('multipleSelectionContainer', { static: true }) multipleSelectionContainer!: ElementRef<HTMLDivElement>;

    loading = input<boolean>(true);
    isOpen = model<boolean>(false);

    cancelSelection = output<void>();
    applySelection = output<T[]>();

    config = input.required<IGenericTypeAheadDropdownConfig<T>>();

    selectedItems = input<T[]>([]);
    allItems = input<T[]>([]);

    searchText = signal('');

    originalItems = signal<T[]>([]);
    tempItems = signal<T[]>([]);

    selectedAtOpenIds = signal<Set<any>>(new Set());
    itemsOrderedOnOpen = signal<T[]>([]);

    filteredItems = computed(() => {
        const search = this.searchText().toLowerCase().trim();
        const items = this.itemsOrderedOnOpen();

        if (!search) return items;

        return items.filter((item) => String(item[this.config().labelKey]).toLowerCase().includes(search));
    });

    applyDisabled = computed(() => this.areEqual(this.originalItems(), this.tempItems()));

    clearDisabled = computed(() => this.tempItems().length === 0);

    overlayWidth = signal<number>(0);

    protected scrollStrategy!: ScrollStrategy;

    private readonly overlay = inject(Overlay);

    constructor() {
        this.scrollStrategy = this.overlay.scrollStrategies.close();

        effect(() => {
            const selected = this.selectedItems();
            this.originalItems.set([...selected]);
            this.tempItems.set([...selected]);
        });

        effect(() => {
            if (!this.isOpen()) return;

            const items = this.allItems();
            if (!items.length) return;

            this.itemsOrderedOnOpen.set(this.orderItemsByInitialSelection(items, this.selectedAtOpenIds()));
        });
    }

    ngAfterViewInit(): void {
        if (!this.isOpen()) {
            this.openPanel();
        }
    }

    openPanel(event?: Event): void {
        if (this.isOpen()) return;
        event?.preventDefault();
        event?.stopPropagation();

        this.resetSearch();
        this.syncTempSelection();
        this.captureSelectionSnapshot();

        setTimeout(() => {
            this.overlayWidth.set(this.multipleSelectionContainer.nativeElement.offsetWidth);
            this.searchInput?.inputElement?.nativeElement.focus();
            this.isOpen.set(true);
        });
    }

    closePanel(): void {
        this.isOpen.set(false);
        this.blurSearch();
        this.cancelSelection.emit();
    }

    cancel(): void {
        this.tempItems.set([...this.originalItems()]);
        this.isOpen.set(false);
        this.blurSearch();
        this.cancelSelection.emit();
    }

    apply(): void {
        this.isOpen.set(false);
        this.applySelection.emit(this.tempItems());
    }

    onSearchChanged(text: string): void {
        this.searchText.set(text ?? '');
    }

    onKeyDown(event: KeyboardEvent): void {
        if (event.key === 'Escape') {
            event.preventDefault();
            this.cancel();
        }
    }

    isTempSelected(item: T): boolean {
        const key = this.config().identityKey;

        return this.tempItems().some((i) => i[key] === item[key]);
    }

    toggleItem(item: T): void {
        const key = this.config().identityKey;

        if (this.isTempSelected(item)) {
            this.tempItems.update((items) => items.filter((i) => i[key] !== item[key]));
        } else {
            this.tempItems.update((items) => [...items, item]);
        }
    }

    clearVisible(): void {
        const key = this.config().identityKey;

        const visibleIds = new Set(this.filteredItems().map((item) => item[key]));

        this.tempItems.update((items) => items.filter((item) => !visibleIds.has(item[key])));
    }

    private blurSearch(): void {
        this.searchInput?.inputElement?.nativeElement.blur();
    }

    private resetSearch(): void {
        this.searchText.set('');
    }

    private syncTempSelection(): void {
        this.tempItems.set([...this.selectedItems()]);
    }

    private captureSelectionSnapshot(): void {
        const key = this.config().identityKey;
        this.selectedAtOpenIds.set(new Set(this.selectedItems().map((item) => item[key])));
    }

    private areEqual(a: T[], b: T[]): boolean {
        if (a.length !== b.length) return false;

        const key = this.config().identityKey;

        const aIds = a.map((i) => i[key]).sort();
        const bIds = b.map((i) => i[key]).sort();

        return aIds.every((id, idx) => id === bIds[idx]);
    }

    private orderItemsByInitialSelection(items: T[], selectedIds: Set<any>): T[] {
        const key = this.config().identityKey;

        return [...items].sort((a, b) => {
            const aSelected = selectedIds.has(a[key]);
            const bSelected = selectedIds.has(b[key]);

            if (aSelected === bSelected) return 0;
            return aSelected ? -1 : 1;
        });
    }
}
